// Define table columns for wellness management

export const nutritionColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name)
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    filters: [
      { text: 'Mediterranean', value: 'mediterranean' },
      { text: 'Vegan', value: 'vegan' },
      { text: 'Vegetarian', value: 'vegetarian' },
      { text: 'Ketogenic', value: 'ketogenic' },
      { text: 'Low-Carb', value: 'low-carb' },
      { text: 'Balanced', value: 'balanced' }
    ],
    onFilter: (value, record) => record.type.indexOf(value) === 0
  },
  {
    title: 'Frequency',
    dataIndex: 'frequency',
    key: 'frequency'
  },
  {
    title: 'Restrictions',
    dataIndex: 'restrictions',
    key: 'restrictions',
    render: (restrictions) => (restrictions ? restrictions.join(', ') : '')
  },
  {
    title: 'Goals',
    dataIndex: 'goals',
    key: 'goals',
    render: (goals) => (goals ? goals.join(', ') : '')
  }
]

export const activityColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name)
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    filters: [
      { text: 'Walking', value: 'walking' },
      { text: 'Cycling', value: 'cycling' },
      { text: 'Swimming', value: 'swimming' },
      { text: 'Yoga', value: 'yoga' },
      { text: 'Strength Training', value: 'strength-training' }
    ],
    onFilter: (value, record) => record.type.indexOf(value) === 0
  },
  {
    title: 'Duration (minutes)',
    dataIndex: 'duration',
    key: 'duration',
    sorter: (a, b) => a.duration - b.duration
  },
  {
    title: 'Frequency',
    dataIndex: 'frequency',
    key: 'frequency'
  },
  {
    title: 'Intensity',
    dataIndex: 'intensity',
    key: 'intensity',
    filters: [
      { text: 'Low', value: 'low' },
      { text: 'Moderate', value: 'moderate' },
      { text: 'High', value: 'high' }
    ],
    onFilter: (value, record) => record.intensity === value
  }
]

export const stressManagementColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name)
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    filters: [
      { text: 'Meditation', value: 'meditation' },
      { text: 'Deep Breathing', value: 'deep-breathing' },
      { text: 'Yoga', value: 'yoga' },
      { text: 'Mindfulness', value: 'mindfulness' }
    ],
    onFilter: (value, record) => record.type.indexOf(value) === 0
  },
  {
    title: 'Duration (minutes)',
    dataIndex: 'duration',
    key: 'duration',
    sorter: (a, b) => a.duration - b.duration
  },
  {
    title: 'Frequency',
    dataIndex: 'frequency',
    key: 'frequency'
  },
  {
    title: 'Benefits',
    dataIndex: 'benefits',
    key: 'benefits',
    render: (benefits) => (benefits ? benefits.join(', ') : '')
  }
]

export const sleepColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name)
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    filters: [
      { text: 'Sleep Hygiene', value: 'sleep-hygiene' },
      { text: 'Bedtime Routine', value: 'bedtime-routine' },
      { text: 'Sleep Environment', value: 'bedroom-environment' }
    ],
    onFilter: (value, record) => record.type.indexOf(value) === 0
  },
  {
    title: 'Target Duration (hours)',
    dataIndex: 'duration',
    key: 'duration',
    sorter: (a, b) => a.duration - b.duration
  },
  {
    title: 'Frequency',
    dataIndex: 'frequency',
    key: 'frequency'
  },
  {
    title: 'Goals',
    dataIndex: 'goals',
    key: 'goals',
    render: (goals) => (goals ? goals.join(', ') : '')
  }
]

export const goalsColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name)
  },
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'category',
    filters: [
      { text: 'Physical Activity', value: 'physical-activity' },
      { text: 'Nutrition', value: 'nutrition' },
      { text: 'Weight Management', value: 'weight-management' },
      { text: 'Sleep', value: 'sleep' },
      { text: 'Stress Management', value: 'stress-management' }
    ],
    onFilter: (value, record) => record.category === value
  },
  {
    title: 'Target',
    dataIndex: 'targetValue',
    key: 'targetValue',
    render: (text, record) => `${text} ${record.targetUnit}`
  },
  {
    title: 'Timeframe',
    dataIndex: 'timeframe',
    key: 'timeframe'
  },
  {
    title: 'Tracking Method',
    dataIndex: 'trackingMethod',
    key: 'trackingMethod'
  }
]

export const educationColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name)
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    filters: [
      { text: 'Nutrition Guide', value: 'nutrition-guide' },
      { text: 'Exercise Tutorial', value: 'exercise-tutorial' },
      { text: 'Stress Management', value: 'stress-management-guide' },
      { text: 'Sleep Guide', value: 'sleep-improvement-guide' }
    ],
    onFilter: (value, record) => record.type === value
  },
  {
    title: 'Format',
    dataIndex: 'format',
    key: 'format',
    filters: [
      { text: 'PDF', value: 'pdf' },
      { text: 'Video', value: 'video' },
      { text: 'Article', value: 'article' },
      { text: 'Interactive', value: 'interactive' }
    ],
    onFilter: (value, record) => record.format === value
  },
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'category'
  },
  {
    title: 'Assignment Schedule',
    dataIndex: 'assignmentSchedule',
    key: 'assignmentSchedule'
  }
]
